import { useState } from "react";
import { BarChart3, Printer, FileSpreadsheet, Search, Loader2, Filter, UserX, PieChart as PieIcon } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../lib/api";
import { STAGES, STAGE_KEYS, GRADES_BY_STAGE, sectionsForGrade, ACADEMIC_YEARS, CURRENT_ACADEMIC_YEAR, SCHOOL_LOGO, SCHOOL_NAME } from "../lib/constants";
import { attendanceReports } from "../lib/api";
import { exportToExcel } from "../lib/excel";

const QUICK = [
  { key: "today", label: "اليوم" },
  { key: "week", label: "الأسبوع" },
  { key: "month", label: "الشهر" },
  { key: "term", label: "الفصل الدراسي" },
  { key: "year", label: "العام الدراسي" },
];

function rangeFor(key) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const end = iso(today);
  const start = new Date(today);
  if (key === "today") return { start: end, end };
  if (key === "week") { start.setDate(start.getDate() - 6); return { start: iso(start), end }; }
  if (key === "month") { start.setDate(1); return { start: iso(start), end }; }
  if (key === "term") { start.setMonth(start.getMonth() - 4); return { start: iso(start), end }; }
  if (key === "year") { start.setFullYear(start.getFullYear() - 1); return { start: iso(start), end }; }
  return { start: end, end };
}

export default function AttendanceReports() {
  const [period, setPeriod] = useState("week");
  const [{ start, end }, setRange] = useState(rangeFor("week"));
  const [stage, setStage] = useState("all");
  const [grade, setGrade] = useState("all");
  const [section, setSection] = useState("all");
  const [year, setYear] = useState(CURRENT_ACADEMIC_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const grades = stage !== "all" ? GRADES_BY_STAGE[stage] : [];
  const sections = grade !== "all" ? sectionsForGrade(grade) : [];

  const setQuick = (k) => {
    setPeriod(k);
    setRange(rangeFor(k));
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { start_date: start, end_date: end };
      if (stage !== "all") params.stage = stage;
      if (grade !== "all") params.grade = grade;
      if (section !== "all") params.section = section;
      if (year) params.academic_year = year;
      setData(await attendanceReports(params));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      setLoading(false);
    }
  };

  const handleExcel = () => {
    if (!data) return;
    // Build absent students detail list
    const absentList = [];
    (data.sessions || []).forEach(s => {
      (s.records || []).filter(r => r.status === "absent").forEach(r => {
        absentList.push({
          date: s.date,
          name: r.name,
          national_id: r.national_id || "",
          stage_label: STAGES[s.stage] || s.stage,
          grade: s.grade,
          section: s.section,
        });
      });
    });
    absentList.sort((a, b) => b.date.localeCompare(a.date));

    exportToExcel(`تقرير_الغياب_${start}_إلى_${end}.xlsx`, [
      {
        name: "ملخص الطلاب",
        columns: [
          { key: "name", header: "اسم الطالب" },
          { key: "national_id", header: "رقم الهوية" },
          { key: "grade", header: "الصف" },
          { key: "section", header: "الفصل" },
          { key: "present", header: "أيام الحضور" },
          { key: "absent", header: "أيام الغياب" },
          { key: "total", header: "إجمالي الأيام" },
        ],
        rows: data.summary,
      },
      {
        name: "تفاصيل الغائبين",
        columns: [
          { key: "date", header: "التاريخ" },
          { key: "name", header: "اسم الطالب" },
          { key: "national_id", header: "رقم الهوية" },
          { key: "stage_label", header: "المرحلة" },
          { key: "grade", header: "الصف" },
          { key: "section", header: "الفصل" },
        ],
        rows: absentList,
      },
      {
        name: "جلسات الغياب",
        columns: [
          { key: "date", header: "التاريخ" },
          { key: "stage_label", header: "المرحلة" },
          { key: "grade", header: "الصف" },
          { key: "section", header: "الفصل" },
          { key: "absent_count", header: "عدد الغائبين" },
          { key: "teacher_name", header: "المعلم" },
        ],
        rows: (data.sessions || []).map(s => ({
          ...s,
          stage_label: STAGES[s.stage] || s.stage,
          absent_count: (s.records || []).filter(r => r.status === "absent").length,
        })),
      },
    ]);
    toast.success("تم تنزيل ملف Excel");
  };

  const handlePrint = () => {
    if (!data) return;
    const totalP = data.summary.reduce((s, r) => s + r.present, 0);
    const totalA = data.summary.reduce((s, r) => s + r.absent, 0);
    const absentList = [];
    (data.sessions || []).forEach(s => {
      (s.records || []).filter(r => r.status === "absent").forEach(r => {
        absentList.push({ date: s.date, name: r.name, national_id: r.national_id || "", stage: s.stage, grade: s.grade, section: s.section });
      });
    });
    absentList.sort((a, b) => b.date.localeCompare(a.date));

    const today = new Date().toLocaleDateString("ar-SA-u-nu-latn");
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return;

    const absentRowsHtml = absentList.length === 0
      ? `<tr><td colspan="7" style="text-align:center;color:#64748B;padding:18px">لا غياب 🎉</td></tr>`
      : absentList.map((r, i) => `<tr><td style="text-align:center">${i + 1}</td><td dir="ltr" style="font-variant-numeric:tabular-nums">${r.date}</td><td>${r.name}</td><td dir="ltr">${r.national_id || "—"}</td><td>${STAGES[r.stage] || ""}</td><td>${r.grade}</td><td>${r.section}</td></tr>`).join("");

    const summaryRowsHtml = data.summary.length === 0
      ? `<tr><td colspan="8" style="text-align:center;color:#64748B;padding:18px">لا توجد بيانات</td></tr>`
      : data.summary.slice(0, 50).map((r, i) => {
          const pct = r.total > 0 ? Math.round((r.absent / r.total) * 100) : 0;
          const cls = pct >= 20 ? "color:#BE123C;font-weight:700" : pct >= 10 ? "color:#B45309;font-weight:700" : "color:#0F766E";
          return `<tr><td style="text-align:center">${i + 1}</td><td>${r.name}</td><td dir="ltr">${r.national_id || "—"}</td><td>${r.grade}</td><td>${r.section}</td><td style="text-align:center;color:#0F766E;font-weight:700">${r.present}</td><td style="text-align:center;color:#BE123C;font-weight:700">${r.absent}</td><td style="text-align:center;${cls}">${pct}%</td></tr>`;
        }).join("");

    w.document.write(`<!doctype html><html lang="ar" dir="rtl"><head><meta charset="utf-8"><title>تقرير الغياب</title>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@500;700;800&family=Tajawal:wght@400;500;700&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box}
        body{font-family:'Tajawal','Cairo',sans-serif;padding:22px 28px;color:#0F172A}
        .head{display:flex;gap:14px;align-items:center;border-bottom:2px solid #0F766E;padding-bottom:12px}
        .head img{width:68px;height:68px;object-fit:contain}
        .school{font-family:'Cairo',sans-serif;font-size:19px;font-weight:800;color:#115E59}
        h1{font-family:'Cairo',sans-serif;font-size:21px;color:#0F766E;margin:6px 0 0}
        .topRow{display:flex;justify-content:space-between;align-items:center;margin-top:14px}
        .stamp{color:#0F766E;font-weight:700;font-size:13px}
        .meta{display:flex;flex-wrap:wrap;gap:14px;margin:10px 0 14px;font-size:12px;color:#334155}
        .meta span{color:#64748B}
        .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px}
        .stat{border-radius:10px;padding:10px 12px;color:#fff}
        .stat .l{font-size:11px;opacity:.9;font-family:'Cairo',sans-serif}
        .stat .v{font-size:22px;font-weight:800;font-variant-numeric:tabular-nums;margin-top:2px}
        .teal{background:linear-gradient(135deg,#0D9488,#0F766E)}
        .rose{background:linear-gradient(135deg,#F43F5E,#E11D48)}
        .slate{background:linear-gradient(135deg,#475569,#334155)}
        .gold{background:linear-gradient(135deg,#F59E0B,#D97706)}
        .barWrap{height:18px;background:#E2E8F0;border-radius:99px;overflow:hidden;display:flex;margin:10px 0 16px}
        .barP{background:#0F766E;display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-family:'Cairo',sans-serif}
        .barA{background:#EF4444;display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-family:'Cairo',sans-serif}
        h2{font-family:'Cairo',sans-serif;font-size:15px;color:#0F766E;margin:18px 0 8px;padding-bottom:6px;border-bottom:1px solid #E2E8F0}
        table{width:100%;border-collapse:collapse;font-size:11.5px}
        thead th{background:#0F766E;color:#fff;padding:8px 6px;text-align:right;font-family:'Cairo',sans-serif;font-weight:700}
        tbody td{padding:6px;border-bottom:1px solid #E2E8F0;text-align:right}
        tbody tr:nth-child(even) td{background:#F8FAFC}
        .footer{margin-top:18px;display:flex;justify-content:space-between;color:#64748B;font-size:11px;border-top:1px dashed #CBD5E1;padding-top:8px}
        .credits{margin-top:6px;display:flex;flex-direction:column;align-items:center;gap:4px;color:#0F766E;font-size:11px;font-family:'Cairo',sans-serif;border-top:1px solid #E2E8F0;padding-top:6px}
        @media print{
          body{padding:12mm}
          .pageBreak{page-break-before:always}
        }
      </style></head><body>
      <div class="head">
        <img src="${SCHOOL_LOGO}"/>
        <div><div class="school">${SCHOOL_NAME}</div><div style="color:#475569;font-size:12px;margin-top:2px">المملكة العربية السعودية — نظام إدارة الطلاب</div></div>
      </div>
      <div class="topRow"><h1>تقرير الغياب</h1><div class="stamp">تاريخ الطباعة: ${today}</div></div>
      <div class="meta">
        <div><span>الفترة:</span> <strong dir="ltr">${start} → ${end}</strong></div>
        <div><span>المرحلة:</span> <strong>${stage === "all" ? "الكل" : STAGES[stage]}</strong></div>
        <div><span>الصف:</span> <strong>${grade === "all" ? "الكل" : grade}</strong></div>
        <div><span>الفصل:</span> <strong>${section === "all" ? "الكل" : section}</strong></div>
        <div><span>العام:</span> <strong>${year}</strong></div>
      </div>

      <div class="stats">
        <div class="stat teal"><div class="l">إجمالي الحضور</div><div class="v">${totalP}</div></div>
        <div class="stat rose"><div class="l">إجمالي الغياب</div><div class="v">${totalA}</div></div>
        <div class="stat slate"><div class="l">عدد الجلسات</div><div class="v">${data.sessions_count}</div></div>
        <div class="stat gold"><div class="l">عدد الطلاب</div><div class="v">${data.summary.length}</div></div>
      </div>

      <div style="font-size:11px;color:#64748B;margin-bottom:4px;font-family:'Cairo',sans-serif">نسبة الحضور إلى الغياب</div>
      <div class="barWrap">
        ${totalP + totalA > 0 ? `<div class="barP" style="width:${(totalP / (totalP + totalA)) * 100}%">حاضرون ${totalP}</div><div class="barA" style="width:${(totalA / (totalP + totalA)) * 100}%">غائبون ${totalA}</div>` : `<div style="width:100%;text-align:center;color:#64748B;font-size:11px">لا بيانات</div>`}
      </div>

      <h2>ملخص الطلاب (مرتب حسب أكثر غياباً)</h2>
      <table><thead><tr><th style="width:36px;text-align:center">#</th><th>اسم الطالب</th><th>رقم الهوية</th><th>الصف</th><th>الفصل</th><th style="text-align:center">حضور</th><th style="text-align:center">غياب</th><th style="text-align:center">النسبة</th></tr></thead><tbody>${summaryRowsHtml}</tbody></table>

      <h2 style="margin-top:22px">قائمة الغائبين بالتفصيل (${absentList.length})</h2>
      <table><thead><tr><th style="width:36px;text-align:center">#</th><th>التاريخ</th><th>اسم الطالب</th><th>رقم الهوية</th><th>المرحلة</th><th>الصف</th><th>الفصل</th></tr></thead><tbody>${absentRowsHtml}</tbody></table>

      <div class="footer"><div>تم إنشاء التقرير بواسطة نظام إدارة الطلاب</div><div>© ${new Date().getFullYear()} — ${SCHOOL_NAME}</div></div>
      <div class="credits"><div>إدارة المدرسة: <strong>الأستاذ محمد عبدالله الصبحي</strong></div><div>برمجة وتصميم: <strong>الأستاذ محمد كامل</strong> • <span dir="ltr">0590939015</span></div></div>
      <script>window.onload=()=>setTimeout(()=>window.print(),400)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <PageHeader title="تقارير الغياب" subtitle="تقارير يومية وأسبوعية وشهرية وفصلية وسنوية مع تصدير Excel و PDF" icon={BarChart3} />

      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {QUICK.map(q => (
            <Button key={q.key} variant={period === q.key ? "default" : "outline"} onClick={() => setQuick(q.key)} className={`h-9 px-3 text-xs ${period === q.key ? "bg-teal-700 hover:bg-teal-800 text-white" : ""}`} data-testid={`period-${q.key}`}>
              {q.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label>من تاريخ</Label>
            <Input type="date" value={start} onChange={(e) => setRange({ start: e.target.value, end })} className="h-10" data-testid="rep-start" />
          </div>
          <div className="space-y-1.5">
            <Label>إلى تاريخ</Label>
            <Input type="date" value={end} onChange={(e) => setRange({ start, end: e.target.value })} className="h-10" data-testid="rep-end" />
          </div>
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v); setGrade("all"); setSection("all"); }}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع المراحل</SelectItem>
                {STAGE_KEYS.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>العام الدراسي</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                {ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {stage !== "all" && (
            <div className="space-y-1.5">
              <Label>الصف</Label>
              <Select value={grade} onValueChange={(v) => { setGrade(v); setSection("all"); }}>
                <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
                <SelectContent dir="rtl">
                  <SelectItem value="all">جميع الصفوف</SelectItem>
                  {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {grade !== "all" && stage !== "all" && (
            <div className="space-y-1.5">
              <Label>الفصل</Label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
                <SelectContent dir="rtl">
                  <SelectItem value="all">جميع الفصول</SelectItem>
                  {sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          {stage !== "all" && start === end && (
            <Button
              onClick={() => window.open(`${API}/absence-report?stage=${stage}&date=${start}`, "_blank")}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-2 h-10 px-5"
              data-testid="btn-official-report"
            >
              <FileSpreadsheet size={16} /> تنزيل الكشف الرسمي
            </Button>
          )}
          <Button onClick={fetchReport} disabled={loading} className="bg-teal-700 hover:bg-teal-800 text-white gap-2 h-10 px-5 btn-elevate" data-testid="btn-search">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
            بحث
          </Button>
        </div>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Card className="p-4 border-slate-200">
              <div className="text-xs text-slate-500">عدد الجلسات</div>
              <div className="text-2xl font-extrabold text-slate-800 tabular-nums">{data.sessions_count}</div>
            </Card>
            <Card className="p-4 border-teal-200 bg-teal-50/40">
              <div className="text-xs text-slate-500">إجمالي الحضور</div>
              <div className="text-2xl font-extrabold text-teal-700 tabular-nums">{data.summary.reduce((s, r) => s + r.present, 0)}</div>
            </Card>
            <Card className="p-4 border-rose-200 bg-rose-50/40">
              <div className="text-xs text-slate-500">إجمالي الغياب</div>
              <div className="text-2xl font-extrabold text-rose-700 tabular-nums">{data.summary.reduce((s, r) => s + r.absent, 0)}</div>
            </Card>
            <Card className="p-4 border-amber-200 bg-amber-50/40">
              <div className="text-xs text-slate-500">عدد الطلاب</div>
              <div className="text-2xl font-extrabold text-amber-700 tabular-nums">{data.summary.length}</div>
            </Card>
          </div>

          {data.unrecorded_classes && data.unrecorded_classes.length > 0 && (
            <Card className="p-4 border-orange-300 bg-orange-50/60 mb-5" data-testid="unrecorded-classes-card">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-orange-600 font-bold text-sm">⚠️ فصول لم يُسجَّل لها غياب خلال هذه الفترة ({data.unrecorded_classes.length})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.unrecorded_classes.map((c, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-lg bg-white border border-orange-200 text-orange-800 text-xs font-medium">
                    {STAGES[c.stage] || c.stage} — {c.grade} — {c.section}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Charts row */}
          {data.sessions_count > 0 && (() => {
            const totalP = data.summary.reduce((s, r) => s + r.present, 0);
            const totalA = data.summary.reduce((s, r) => s + r.absent, 0);
            const pieData = [
              { name: "حاضرون", value: totalP, color: "#0F766E" },
              { name: "غائبون", value: totalA, color: "#EF4444" },
            ];
            // Per-day aggregation
            const dailyMap = {};
            data.sessions.forEach(s => {
              const d = s.date;
              if (!dailyMap[d]) dailyMap[d] = { date: d, present: 0, absent: 0 };
              (s.records || []).forEach(r => {
                if (r.status === "absent") dailyMap[d].absent++;
                else dailyMap[d].present++;
              });
            });
            const dailyData = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));
            // Absent students flat list (with date, grade, section)
            const absentList = [];
            data.sessions.forEach(s => {
              (s.records || []).filter(r => r.status === "absent").forEach(r => {
                absentList.push({
                  date: s.date,
                  name: r.name,
                  national_id: r.national_id || "",
                  stage: s.stage,
                  grade: s.grade,
                  section: s.section,
                });
              });
            });
            absentList.sort((a, b) => b.date.localeCompare(a.date));
            return (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 mb-5">
                  <Card className="p-4 sm:p-5 border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900">نسبة الحضور إلى الغياب</h3>
                      <PieIcon className="text-teal-700 shrink-0" size={18} />
                    </div>
                    <div className="h-64 sm:h-72 w-full" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieData} dataKey="value" innerRadius={55} outerRadius={95} paddingAngle={3} label={(d) => `${((d.percent || 0) * 100).toFixed(0)}%`}>
                            {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontFamily: "Tajawal", fontSize: 13 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <Card className="lg:col-span-2 p-4 sm:p-5 border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900">الحضور والغياب اليومي</h3>
                      <BarChart3 className="text-teal-700 shrink-0" size={18} />
                    </div>
                    <div className="h-64 sm:h-72 w-full" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dailyData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} angle={-30} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#475569" }} width={30} />
                          <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontFamily: "Tajawal", fontSize: 12 }} formatter={(v) => v === "present" ? "حاضرون" : "غائبون"} />
                          <Bar dataKey="present" fill="#0F766E" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="absent" fill="#EF4444" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </div>

                {/* Top absentees + Trend */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 mb-5">
                  <Card className="p-4 sm:p-5 border-slate-200">
                    <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900 mb-3">أعلى 10 طلاب غياباً</h3>
                    <div className="h-64 sm:h-72 w-full" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.summary.slice(0, 10).reverse()} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#475569" }} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} width={110} />
                          <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} formatter={(v) => [v, "أيام الغياب"]} />
                          <Bar dataKey="absent" fill="#EF4444" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <Card className="lg:col-span-2 p-4 sm:p-5 border-slate-200">
                    <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900 mb-3">اتجاه الغياب عبر الفترة</h3>
                    <div className="h-64 sm:h-72 w-full" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dailyData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} angle={-30} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#475569" }} width={30} />
                          <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                          <Line type="monotone" dataKey="absent" stroke="#EF4444" strokeWidth={2.5} dot={{ r: 4, fill: "#EF4444" }} name="غائبون" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </div>

                {/* Absent students detail list */}
                <Card className="border-slate-200 overflow-hidden mb-5">
                  <div className="p-3 sm:p-4 border-b border-slate-100 bg-rose-50/60 flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-rose-600 text-white flex items-center justify-center"><UserX size={16} /></div>
                    <div>
                      <h3 className="font-cairo font-bold text-rose-900">قائمة الطلاب الغائبين</h3>
                      <div className="text-xs text-rose-700/80">{absentList.length} حالة غياب — مرتبة من الأحدث</div>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-xs sm:text-sm min-w-[720px]">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-slate-700">
                          <th className="px-3 py-2.5 text-center font-cairo w-12">#</th>
                          <th className="px-3 py-2.5 text-right font-cairo">التاريخ</th>
                          <th className="px-3 py-2.5 text-right font-cairo">اسم الطالب</th>
                          <th className="px-3 py-2.5 text-right font-cairo">رقم الهوية</th>
                          <th className="px-3 py-2.5 text-right font-cairo">المرحلة</th>
                          <th className="px-3 py-2.5 text-right font-cairo">الصف</th>
                          <th className="px-3 py-2.5 text-right font-cairo">الفصل</th>
                        </tr>
                      </thead>
                      <tbody>
                        {absentList.length === 0 ? (
                          <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">لا غياب في هذه الفترة 🎉</td></tr>
                        ) : absentList.map((r, i) => (
                          <tr key={i} className="odd:bg-white even:bg-slate-50/60 hover:bg-rose-50/40" data-testid={`absent-row-${i}`}>
                            <td className="px-3 py-2 text-center text-slate-500 tabular-nums">{i + 1}</td>
                            <td className="px-3 py-2 text-slate-700 tabular-nums whitespace-nowrap" dir="ltr">{r.date}</td>
                            <td className="px-3 py-2 text-slate-800">{r.name}</td>
                            <td className="px-3 py-2 text-slate-700 tabular-nums" dir="ltr">{r.national_id || "—"}</td>
                            <td className="px-3 py-2 text-slate-700">{STAGES[r.stage] || "—"}</td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.grade}</td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.section}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            );
          })()}

          <Card className="border-slate-200 overflow-hidden">
            <div className="p-3 border-b border-slate-100 bg-slate-50/60 flex flex-wrap justify-between gap-2 items-center">
              <div className="flex items-center gap-2 text-sm text-slate-700 font-cairo font-bold"><Filter size={14} /> ملخص الطلاب — مرتب حسب أكثر غياباً</div>
              <div className="flex items-center gap-2">
                <Button onClick={handleExcel} variant="outline" className="h-9 gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-export-excel">
                  <FileSpreadsheet size={15} /> Excel
                </Button>
                <Button onClick={handlePrint} className="h-9 gap-2 bg-teal-700 hover:bg-teal-800 text-white" data-testid="btn-print-attendance">
                  <Printer size={15} /> طباعة / PDF
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm min-w-[700px]">
                <thead className="bg-teal-700 text-white">
                  <tr>
                    <th className="px-3 py-2.5 text-center font-cairo w-12">#</th>
                    <th className="px-3 py-2.5 text-right font-cairo">اسم الطالب</th>
                    <th className="px-3 py-2.5 text-right font-cairo">رقم الهوية</th>
                    <th className="px-3 py-2.5 text-right font-cairo">الصف</th>
                    <th className="px-3 py-2.5 text-right font-cairo">الفصل</th>
                    <th className="px-3 py-2.5 text-center font-cairo">الحضور</th>
                    <th className="px-3 py-2.5 text-center font-cairo">الغياب</th>
                    <th className="px-3 py-2.5 text-center font-cairo">النسبة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-400">لا توجد بيانات في الفترة المحددة</td></tr>
                  ) : data.summary.map((r, i) => {
                    const pct = r.total > 0 ? ((r.absent / r.total) * 100).toFixed(0) : 0;
                    return (
                      <tr key={i} className="odd:bg-white even:bg-slate-50/60">
                        <td className="px-3 py-2 text-center text-slate-500 tabular-nums">{i + 1}</td>
                        <td className="px-3 py-2 text-slate-800">{r.name}</td>
                        <td className="px-3 py-2 text-slate-700 tabular-nums" dir="ltr">{r.national_id || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">{r.grade}</td>
                        <td className="px-3 py-2 text-slate-700">{r.section}</td>
                        <td className="px-3 py-2 text-center text-teal-700 tabular-nums font-bold">{r.present}</td>
                        <td className="px-3 py-2 text-center text-rose-600 tabular-nums font-bold">{r.absent}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-1 rounded text-xs tabular-nums font-bold ${pct >= 20 ? "bg-rose-100 text-rose-700" : pct >= 10 ? "bg-amber-100 text-amber-700" : "bg-teal-100 text-teal-700"}`}>
                            {pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
